// @flow
import type {Node} from 'react'
import React, {Suspense, Fragment, useEffect} from 'react'
import {RelayEnvironmentProvider} from 'react-relay'
import queryString from 'query-string'
import {BrowserRouter, Route, Redirect, Switch} from 'react-router-dom'

import environment from 'environment'
import OrgnavRouter from 'utils/OrgnavRouter'
import AppRouter, {withAppRouterSetup} from 'utils/AppRouter'
import PageTitle from 'components/ui/PageTitle'
import Preload from 'components/ui/Preload'
import LayoutedPreload from 'components/ui/LayoutedPreload'
import Layout from 'components/ui/Layout'
import OrganizationContextProviderQuery from 'contexts/OrganizationContextProviderQuery'
import RedirectGuestToLogin from './RedirectGuestToLogin'
import RootErrorBoundary from './RootErrorBoundary'
import styles from './index.scss'

const ProjectsPage = React.lazy(() => import(/* webpackChunkName: "ProjectsPage" */ 'components/pages/Projects'))
const ActionsPage = React.lazy(() => import(/* webpackChunkName: "ActionsPage" */ 'components/pages/Actions'))
const AgendaItemsPage = React.lazy(() => import(/* webpackChunkName: "AgendaItemsPage" */ 'components/pages/AgendaItems'))
const TensionsPage = React.lazy(() => import(/* webpackChunkName: "TensionsPage" */ 'components/pages/Tensions'))
const ChecklistsPage = React.lazy(() => import(/* webpackChunkName: "ChecklistPage" */ 'components/pages/Checklists'))
const MetricsPage = React.lazy(() => import(/* webpackChunkName: "MetricsPage" */ 'components/pages/Metrics'))
const GoalsPage = React.lazy(() => import(/* webpackChunkName: "GoalsPage" */ 'components/pages/Goals'))
const DomainPage = React.lazy(() => import(/* webpackChunkName: "DomainPage" */ 'components/pages/Domain'))
const PolicyPage = React.lazy(() => import(/* webpackChunkName: "PolicyPage" */ 'components/pages/Policy'))
const RoleNotePage = React.lazy(() => import(/* webpackChunkName: "RoleNotePage" */ 'components/pages/RoleNote'))
const RoleContainer = React.lazy(() => import(/* webpackChunkName: "RoleContainer" */ './RoleContainer'))
const SearchPage = React.lazy(() => import(/* webpackChunkName: "SearchPage" */ './SearchPage'))
const ProposalsPage = React.lazy(() => import(/* webpackChunkName: "ProposalsPage" */ 'components/pages/Proposals'))
const TacticalMeetingPage = React.lazy(() => import(/* webpackChunkName: "TacticalMeetingPage" */ 'components/pages/TacticalMeeting'))
const GovernanceMeetingHistoryPage = React.lazy(() => import(/* webpackChunkName: "TacticalMeetingPage" */ 'components/pages/GovernanceMeetingHistory'))
const ReviewProposalPage = React.lazy(() => import(/* webpackChunkName: "ReviewProposalPage" */ 'components/pages/ReviewProposal'))
const CircleIntegrationsPage = React.lazy(() => import(/* webpackChunkName: "CircleIntegrationsPage" */ 'components/pages/CircleIntegrations'))
const CircleJiraPage = React.lazy(() => import(/* webpackChunkName: "CircleJiraPage" */ 'components/pages/CircleIntegrations/CircleJira'))
const AssignmentPage = React.lazy(() => import(/* webpackChunkName: "AssignmentPage" */ 'components/pages/AssignmentHistory'))
const ConstitutionPage = React.lazy(() => import(/* webpackChunkName: "ConstitutionPage" */ 'components/pages/Constitution'))
const WelcomePage = React.lazy(() => import(/* webpackChunkName: "WelcomePage" */ 'components/pages/WelcomePage'))
const GovernanceMeetingPage = React.lazy(() => import(/* webpackChunkName: "GovernanceMeetingPage" */ 'components/pages/GovernanceMeeting'))
const GlobalItems = React.lazy(() => import(/* webpackChunkName: "GovernanceMeetingPage" */ 'components/pages/GlobalItems'))

type Props = {
  organizationId: string, // GraphQL ID
  organizationDatabaseId: string,
  organizationName: string,
  anchorRoleDatabaseId: string,
  viewerDatabaseId: ?number,
}

const EnhancedActionsPage = withAppRouterSetup(ActionsPage)
const EnhancedAgendaItemsPage = withAppRouterSetup(AgendaItemsPage)
const EnhancedDomainPage = withAppRouterSetup(DomainPage)
const EnhancedPolicyPage = withAppRouterSetup(PolicyPage)
const EnhancedProjectsPage = withAppRouterSetup(ProjectsPage)
const EnhancedChecklistsPage = withAppRouterSetup(ChecklistsPage)
const EnhancedMetricsPage = withAppRouterSetup(MetricsPage)
const EnhancedGoalsPage = withAppRouterSetup(GoalsPage)
const EnhancedRoleContainer = withAppRouterSetup(RoleContainer)
const EnhancedRoleNotePage = withAppRouterSetup(RoleNotePage)
const EnhancedProposalsPage = withAppRouterSetup(ProposalsPage)
const EnhancedReviewProposalPage = withAppRouterSetup(ReviewProposalPage)
const EnhancedSearchPage = withAppRouterSetup(SearchPage)
const EnhancedTensionsPage = withAppRouterSetup(TensionsPage)
const EnhancedTacticalMeetingPage = withAppRouterSetup(TacticalMeetingPage)
const EnhancedGovernanceMeetingHistoryPage = withAppRouterSetup(GovernanceMeetingHistoryPage)
const EnhancedCircleIntegrationsPage = withAppRouterSetup(CircleIntegrationsPage)
const EnhancedCircleJiraPage = withAppRouterSetup(CircleJiraPage)
const EnhancedAssignmentPage = withAppRouterSetup(AssignmentPage)
const EnhancedConstitutionPage = withAppRouterSetup(ConstitutionPage)
const EnhancedWelcomePage = withAppRouterSetup(WelcomePage)
const EnhancedGovernanceMeetingPage = withAppRouterSetup(GovernanceMeetingPage)
const EnhancedGlobalItems = withAppRouterSetup(GlobalItems)

function OrgNavApp({organizationId, organizationDatabaseId, organizationName, anchorRoleDatabaseId, viewerDatabaseId}
                       : Props): Node {
  const baseUrl = `/organizations/${organizationDatabaseId}/orgnav`

  useEffect(() => {
    AppRouter.instance.setupBaseUrl(baseUrl)
  }, [baseUrl])

  return (
    <RelayEnvironmentProvider environment={environment}>
      <RootErrorBoundary>
        <OrganizationContextProviderQuery orgDatabaseId={organizationDatabaseId}>
          <BrowserRouter basename={baseUrl}>
            <Suspense fallback={null}>
              <Switch>
                <Route
                  exact
                  path="/tactical_meetings/:tacticalMeetingDatabaseId/:tab"
                  render={({match}) => {
                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id={`${match.params.tacticalMeetingDatabaseId}-tactical-meeting`} layout="none">
                        <Suspense fallback={<Preload />}>
                          <EnhancedTacticalMeetingPage
                            tab={match.params.tab}
                            orgDatabaseId={organizationDatabaseId}
                            tacticalMeetingDatabaseId={match.params.tacticalMeetingDatabaseId}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  exact
                  path="/governance_meetings/:govMeetingDatabaseId"
                  render={({match}) => {
                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id={`${match.params.govMeetingDatabaseId}-gov-meeting`} layout="none">
                        <Suspense fallback={<Preload />}>
                          <EnhancedGovernanceMeetingPage
                            orgDatabaseId={organizationDatabaseId}
                            meetingDatabaseId={match.params.govMeetingDatabaseId}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  exact
                  path="/governance_meetings/:meetingDatabaseId/history"
                  render={({match}) => (
                    <Layout id={`${match.params.meetingDatabaseId}-governance-meeting`}>
                      <Suspense fallback={<Preload />}>
                        <EnhancedGovernanceMeetingHistoryPage
                          orgDatabaseId={organizationDatabaseId}
                          meetingDatabaseId={match.params.meetingDatabaseId}
                        />
                      </Suspense>
                    </Layout>
                  )}
                />
                <Route
                  exact
                  path="/roles/:roleId"
                  render={({match}) => {
                    OrgnavRouter.instance.setRoleTab('overview')
                    return (
                      <EnhancedRoleContainer
                        anchorRoleDatabaseId={anchorRoleDatabaseId}
                        organizationDatabaseId={organizationDatabaseId}
                        roleDatabaseId={match.params.roleId}
                        tabName="overview"
                      />
                    )
                  }}
                />
                <Route
                  exact
                  path="/roles/:roleId/assignment_records"
                  render={({match}) => (
                    <EnhancedAssignmentPage
                      orgDatabaseId={organizationDatabaseId}
                      roleDatabaseId={match.params.roleId}
                    />
                  )}
                />
                <Route
                  exact
                  path="/roles/:roleId/integrations"
                  render={({match}) => (
                    <EnhancedCircleIntegrationsPage
                      roleDatabaseId={match.params.roleId}
                      orgDatabaseId={organizationDatabaseId}
                    />
                  )}
                />
                <Route
                  exact
                  path="/roles/:roleId/integrations/jira/:step"
                  render={({match}) => (
                    <EnhancedCircleJiraPage
                      roleDatabaseId={match.params.roleId}
                      orgDatabaseId={organizationDatabaseId}
                      step={match.params.step}
                    />
                  )}
                />
                <Route
                  exact
                  path="/roles/:roleId/:section"
                  render={({match}) => {
                    OrgnavRouter.instance.setRoleTab(match.params.section)
                    return (
                      <EnhancedRoleContainer
                        anchorRoleDatabaseId={anchorRoleDatabaseId}
                        organizationDatabaseId={organizationDatabaseId}
                        roleDatabaseId={match.params.roleId}
                        tabName={match.params.section}
                      />
                    )
                  }}
                />
                <Route
                  path="/roles/:roleId/notes/:noteId"
                  render={({match}) => (
                    <Layout id={`role-note-page-${match.params.noteId}`}>
                      <Suspense fallback={<Preload />}>
                        <EnhancedRoleNotePage
                          orgDatabaseId={organizationDatabaseId}
                          roleNoteDatabaseId={match.params.noteId}
                        />
                      </Suspense>
                    </Layout>
                  )}
                />
                <Route
                  path="/roles/:roleId/domains/:domainId"
                  render={({match}) => (
                    <Layout id={`domain-page-${match.params.domainDatabaseId}`}>
                      <Suspense fallback={<Preload />}>
                        <EnhancedDomainPage
                          orgDatabaseId={organizationDatabaseId}
                          domainDatabaseId={match.params.domainId}
                        />
                      </Suspense>
                    </Layout>
                  )}
                />
                <Route
                  path="/policies/:policyId"
                  render={({match}) => (
                    <Suspense fallback={<LayoutedPreload prefix="policy-page" id={match.params.policyId} />}>
                      <EnhancedPolicyPage
                        orgDatabaseId={organizationDatabaseId}
                        policyDatabaseId={match.params.policyId}
                      />
                    </Suspense>
                  )}
                />
                <Route
                  path="/rules"
                  render={() => {
                    const title = organizationName

                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <div className={styles.pageContainer}>
                        <PageTitle title={title} />
                        <Suspense fallback={<LayoutedPreload id="constitution-page" />}>
                          <EnhancedConstitutionPage
                            orgDatabaseId={organizationDatabaseId}
                            organizationName={organizationName}
                          />
                        </Suspense>
                      </div>
                    )
                  }}
                />
                <Route
                  path="/welcome"
                  render={() => (
                    <Fragment>
                      <PageTitle title={I18n.t('nav.your_holacracy_companion')} />
                      {viewerDatabaseId
                        ? <EnhancedWelcomePage organizationId={organizationId} />
                        : <RedirectGuestToLogin />
                            }
                    </Fragment>
                  )}
                />
                <Route
                  path="/my_actions"
                  render={() => {
                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id="actions-page" test-id="actions-page" layout="workspace">
                        <Suspense fallback={<LayoutedPreload id="my-actions-page" />}>
                          <EnhancedActionsPage
                            orgDatabaseId={organizationDatabaseId}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  path="/search"
                  render={({location}) => {
                    const title = I18n.t('shared.generic_org_page_title', {
                      org_name: organizationName,
                      page_name: I18n.t('nav.search'),
                    })

                    return (
                      <div className={styles.pageContainer}>
                        <PageTitle title={title} />
                        <EnhancedSearchPage
                          orgDatabaseId={organizationDatabaseId}
                          searchQuery={queryString.parse(location.search).search || ''}
                        />
                      </div>
                    )
                  }}
                />
                <Route
                  exact
                  path="/proposals/:proposalId/review"
                  render={({match}) => {
                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Suspense fallback={<LayoutedPreload prefix="proposal-review-page" id={match.params.proposalId} />}>
                        <EnhancedReviewProposalPage
                          orgDatabaseId={organizationDatabaseId}
                          proposalDatabaseId={match.params.proposalId}
                        />
                      </Suspense>
                    )
                  }}
                />
                <Route
                  exact
                  path={['/proposals', '/proposals/:proposalId/edit']}
                  render={({match: {params}}) => {
                    const title = I18n.t('shared.generic_org_page_title', {
                      org_name: organizationName,
                      page_name: I18n.t('nav.proposals'),
                    })

                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id="orgnav-proposals" layout="workspace">
                        <PageTitle title={title} />
                        <Suspense fallback={<LayoutedPreload prefix="proposal-edit-page" id={params.proposalId} />}>
                          <EnhancedProposalsPage
                            activeProposalDatabaseId={params.proposalId}
                            orgDatabaseId={organizationDatabaseId}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  path="/inbox"
                  render={({location}) => {
                    const title = I18n.t('shared.generic_org_page_title', {
                      org_name: organizationName,
                      page_name: I18n.t('nav.inbox'),
                    })

                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id="inbox" layout="workspace">
                        <PageTitle title={title} />
                        <Suspense fallback={<LayoutedPreload id="inbox-page" />}>
                          <EnhancedTensionsPage
                            activeTensionId={queryString.parse(location.search).tension_id || null}
                            orgDatabaseId={organizationDatabaseId}
                            personId={viewerDatabaseId}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  path="/my_projects"
                  render={() => {
                    const title = I18n.t('shared.generic_org_page_title', {
                      org_name: organizationName,
                      page_name: I18n.t('nav.projects'),
                    })

                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id="orgnav-my-projects" layout="workspace">
                        <PageTitle title={title} />
                        <Suspense fallback={<LayoutedPreload id="my-projects-page" />}>
                          <EnhancedProjectsPage
                            orgDatabaseId={organizationDatabaseId}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  path="/checklists"
                  render={() => {
                    const title = I18n.t('shared.generic_org_page_title', {
                      org_name: organizationName,
                      page_name: I18n.t('nav.checklists'),
                    })

                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id="checklists-page" name="Checklists page" layout="workspace">
                        <PageTitle title={title} />
                        <Suspense fallback={<LayoutedPreload id="checklists-page" />}>
                          <EnhancedChecklistsPage
                            orgDatabaseId={organizationDatabaseId}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  path="/metrics"
                  render={() => {
                    const title = I18n.t('shared.generic_org_page_title', {
                      org_name: organizationName,
                      page_name: I18n.t('nav.metrics'),
                    })

                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id="metrics-page" name="Metrics page" layout="workspace">
                        <PageTitle title={title} />
                        <Suspense fallback={<LayoutedPreload id="metrics-page" />}>
                          <EnhancedMetricsPage
                            orgDatabaseId={organizationDatabaseId}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  path="/goals"
                  render={() => {
                    const title = I18n.t('shared.generic_org_page_title', {
                      org_name: organizationName,
                      page_name: I18n.t('nav.goals'),
                    })

                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id="goals-page" name="Goals page" layout="workspace">
                        <PageTitle title={title} />
                        <Suspense fallback={<LayoutedPreload id="goals-page" />}>
                          <EnhancedGoalsPage
                            orgDatabaseId={organizationDatabaseId}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  path="/my_agenda_items"
                  render={({location}) => {
                    const title = I18n.t('shared.generic_org_page_title', {
                      org_name: organizationName,
                      page_name: I18n.t('nav.agenda_items'),
                    })

                    if (!viewerDatabaseId)
                      return <RedirectGuestToLogin />

                    return (
                      <Layout id="agenda-items-page" test-id="agenda-items-page" layout="workspace">
                        <PageTitle title={title} />
                        <Suspense fallback={<LayoutedPreload id="agenda-items-page" />}>
                          <EnhancedAgendaItemsPage
                            orgDatabaseId={organizationDatabaseId}
                            activeTensionId={queryString.parse(location.search).tension_id}
                          />
                        </Suspense>
                      </Layout>
                    )
                  }}
                />
                <Route
                  path="/admin/global_items"
                  render={() => (
                    <Layout id="global_items">
                      <Suspense fallback={<Preload />}>
                        <PageTitle title={I18n.t('nav.global_checklist')} />
                        <EnhancedGlobalItems
                          orgDatabaseId={organizationDatabaseId}
                        />
                      </Suspense>
                    </Layout>
                  )}
                />
                <Route
                  path="/:legacyPath"
                  render={({match}) => (
                    <Redirect to={match.params.legacyPath.replace('role-', 'roles/')} />
                  )}
                />
                <Redirect from="/" to={`/roles/${anchorRoleDatabaseId}`} />
              </Switch>
            </Suspense>
          </BrowserRouter>
        </OrganizationContextProviderQuery>
      </RootErrorBoundary>
    </RelayEnvironmentProvider>
  )
}

OrgNavApp.defaultProps = {
  viewerDatabaseId: null,
}

export default OrgNavApp
